import { useState } from 'react';
import { CheckCircle, FileCheck, Globe, Clock, Shield, Award, Mail, Phone } from 'lucide-react';
import HighlightText from '../components/core/HomePage/HighlightText';
import Footer from '../components/common/Footer';

export default function DocumentationSupport() {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [education, setEducation] = useState('');
  const [service, setService] = useState('');
  const [profession, setProfession] = useState('');
  const [state, setState] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log({ name, phone, email, education, service, profession, state, message });
  };

  return (
    <div className="min-h-screen bg-richblue-5 text-richblue-700">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-richblue-200 to-richblue-100 text-richblue-700 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Facing Difficulties in Your Documentation Process?</h1>
          <p className="text-xl text-richblue-500">
            Choose us for a hassle-free, reliable, and efficient solution to all your academic documentation needs. Let us handle the complexities while you focus on your academic and professional goals.
          </p>
        </div>
      </div>

      {/* Overview Section */}
      <div className="bg-richblue-5 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">One-Stop-Destination for all <HighlightText text={"Documentation Needs"} /></h2>
            <p className="text-richblue-500 mb-8 text-lg">
              Are you facing difficulties in your documentation process? Let us be your helping hand. We are your one-stop destination for all documentation needs, providing comprehensive support services to ensure a smooth and hassle-free experience.
            </p>
            
            <div className="bg-richblue-5 p-8 rounded-lg shadow-md border border-richblue-300 mb-12">
              <h3 className="text-2xl font-bold mb-4 text-richblue-600">Our Documentation Support</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">AIU Evaluation of your Foreign Qualifications</p>
                </div>
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">Duplicate Copy of Marksheets & Degrees from Universities</p>
                </div>
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">Attestation and Apostille Services (All Countries)</p>
                </div>
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">Document Translation Services</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="py-16 bg-richblue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Why Choose <HighlightText text={"Us"} /></h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Award className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Expertise and Experience</h3>
              <p className="text-richblue-500">
                With a team of seasoned professionals, we have extensive experience in handling all types of academic documentation.
              </p>
            </div>

            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Clock className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Efficiency and Timeliness</h3>
              <p className="text-richblue-500">
                We prioritize efficiency and timeliness, ensuring your documentation process is completed quickly without compromising on quality.
              </p>
            </div>

            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Shield className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Reliable and Trustworthy</h3>
              <p className="text-richblue-500">
                We are a trusted name in academic documentation services, handling your documents with utmost care and confidentiality.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="bg-richblue-5 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">How We Help with Your <HighlightText text={"Documentation"} /></h2>
          
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-richblue-5 p-6 rounded-lg border border-richblue-300">
              <h3 className="text-xl font-bold mb-4 text-richblue-600">AIU Evaluation for Foreign Qualifications</h3>
              <p className="text-richblue-500">
                We facilitate the evaluation of your foreign qualifications by the Association of Indian Universities (AIU). This process ensures that your international degrees and certificates are recognized and equivalent to Indian standards.
              </p>
            </div>

            <div className="bg-richblue-5 p-6 rounded-lg border border-richblue-300">
              <h3 className="text-xl font-bold mb-4 text-richblue-600">Document Translation Services</h3>
              <p className="text-richblue-500">
                Our expert translators provide precise and certified translations for your academic documents. We ensure that they meet the required standards and are accepted by institutions and authorities worldwide.
              </p>
            </div>

            <div className="bg-richblue-5 p-6 rounded-lg border border-richblue-300">
              <h3 className="text-xl font-bold mb-4 text-richblue-600">Duplicate Marksheets & Degrees</h3>
              <p className="text-richblue-500">
                We assist you in obtaining duplicates from your university, handling the entire process from contacting the institution to securing the necessary documents.
              </p>
            </div>

            <div className="bg-richblue-5 p-6 rounded-lg border border-richblue-300">
              <h3 className="text-xl font-bold mb-4 text-richblue-600">Attestation and Apostille Services</h3>
              <p className="text-richblue-500">
                We offer comprehensive attestation and apostille services, ensuring your documents are authenticated and valid for use in any country.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Support Services */}
      <div className="py-16 bg-richblue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Support Services We <HighlightText text={"Offer"} /></h2>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="bg-richblue-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-richblue-400" size={24} />
              </div>
              <h4 className="text-lg font-semibold mb-2 text-richblue-600">Proven Track Record</h4>
            </div>
            
            <div className="text-center">
              <div className="bg-richblue-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <FileCheck className="text-richblue-400" size={24} />
              </div>
              <h4 className="text-lg font-semibold mb-2 text-richblue-600">Open and Truthful Fees</h4>
            </div>
            
            <div className="text-center">
              <div className="bg-richblue-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Clock className="text-richblue-400" size={24} />
              </div>
              <h4 className="text-lg font-semibold mb-2 text-richblue-600">24x7 Support & Services</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="bg-richblue-5 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-richblue-600">Get Started Today</h2>
              <p className="text-richblue-500 mb-6">
                Connect with our documentation experts for a free consultation about your documentation needs.
              </p>
              <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 mb-6">
                <h3 className="text-xl font-bold mb-4 text-richblue-600">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Mail className="text-richblue-400 mr-3" size={20} />
                    <span className="text-richblue-500">info@aimlay.com</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="text-richblue-400 mr-3" size={20} />
                    <span className="text-richblue-500">+91 9821664888</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
              <h3 className="text-xl font-bold mb-4 text-richblue-600">Request Information</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-richblue-500 mb-2">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-richblue-500 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-richblue-500 mb-2">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="education" className="block text-richblue-500 mb-2">Last Education Qualification *</label>
                  <input
                    type="text"
                    id="education"
                    value={education}
                    onChange={(e) => setEducation(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="service" className="block text-richblue-500 mb-2">Type of Service *</label>
                  <select
                    id="service"
                    value={service}
                    onChange={(e) => setService(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  >
                    <option value="">Select a Service</option>
                    <option value="attestation">Attestation</option>
                    <option value="apostille">Apostille</option>
                    <option value="aiu">AIU Evaluation</option>
                    <option value="translation">Documents Translation</option>
                    <option value="duplicate">Duplicate Copies from University</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="profession" className="block text-richblue-500 mb-2">Profession *</label>
                  <select
                    id="profession"
                    value={profession}
                    onChange={(e) => setProfession(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  >
                    <option value="">Select Profession</option>
                    <option value="working">Working Professional</option>
                    <option value="student">Student</option>
                    <option value="self-employed">Self Employed</option>
                    <option value="unemployed">Unemployed</option>
                    <option value="retired">Retired</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="state" className="block text-richblue-500 mb-2">State *</label>
                  <select
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  >
                    <option value="">Select State</option>
                    <option value="delhi">Delhi</option>
                    <option value="maharashtra">Maharashtra</option>
                    <option value="karnataka">Karnataka</option>
                    {/* Add more states as needed */}
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-richblue-500 mb-2">Message</label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900 h-32"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-richblue-400 text-white font-semibold py-3 px-6 rounded-md hover:bg-richblue-500 transition-all duration-200"
                >
                  Submit Request
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
} 